import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMobileLayout } from '../../utils/useMobileLayout';

// Renders a list of items as cards on mobile and as a table on desktop.
//
// Props:
//   items:       array of records to render
//   columns:     [{ key, header, render?, align? }]  — desktop table cells
//                If `render` is omitted, item[key] is used.
//   mobileCard:  (item) => ReactNode  — the card body for one item on mobile
//   onRowClick:  (item) => void       — optional; makes rows/cards tappable
//   getKey:      (item) => string|number  — defaults to item.id
//   loading:     boolean
//   empty:       string|ReactNode shown when items is empty
//   dense:       boolean — passed to the desktop Table
const ResponsiveList = ({
  items = [],
  columns = [],
  mobileCard,
  onRowClick,
  getKey = (item) => item.id,
  loading = false,
  empty = 'No records found.',
  dense = false,
}) => {
  const { isMobile } = useMobileLayout();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!items || items.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        {typeof empty === 'string' ? (
          <Typography color="text.secondary">{empty}</Typography>
        ) : (
          empty
        )}
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Stack spacing={1.5}>
        {items.map((item) => {
          const key = getKey(item);
          const body = mobileCard ? mobileCard(item) : (
            // Fallback when no mobileCard is provided: render columns as
            // simple label/value rows. Useful for quick wiring.
            <Stack spacing={0.5}>
              {columns.map((col) => (
                <Box key={col.key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">{col.header}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'right' }}>
                    {col.render ? col.render(item) : item[col.key] ?? '—'}
                  </Typography>
                </Box>
              ))}
            </Stack>
          );

          return (
            <Card key={key} variant="outlined">
              {onRowClick ? (
                <CardActionArea onClick={() => onRowClick(item)}>
                  <CardContent>{body}</CardContent>
                </CardActionArea>
              ) : (
                <CardContent>{body}</CardContent>
              )}
            </Card>
          );
        })}
      </Stack>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table size={dense ? 'small' : 'medium'}>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col.key} align={col.align || 'left'}>
                {col.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow
              key={getKey(item)}
              hover={Boolean(onRowClick)}
              onClick={onRowClick ? () => onRowClick(item) : undefined}
              sx={onRowClick ? { cursor: 'pointer' } : undefined}
            >
              {columns.map((col) => (
                <TableCell key={col.key} align={col.align || 'left'}>
                  {col.render ? col.render(item) : item[col.key] ?? '—'}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ResponsiveList;
